import React, { useEffect, useRef, useState } from "react";
import { X, Send, Mic, MicOff, Volume2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map UI language to SpeechRecognition/TTS locale
const SPEECH_LOCALE = {
  it: "it-IT",
  en: "en-US",
  pa: "pa-IN",
  pa_lat: "pa-IN",
  hi: "hi-IN",
  ur: "ur-PK",
};

// Fallback locale if the primary locale isn't supported by the device
const SPEECH_FALLBACK = {
  pa: "hi-IN",
  pa_lat: "hi-IN",
  ur: "hi-IN",
};

const MIC_HINT = {
  it: "💡 Premi il microfono e parla. Chikoo ti risponde a voce.",
  en: "💡 Press the microphone and speak. Chikoo replies with her voice.",
  pa: "💡 ਮਾਈਕ੍ਰੋਫੋਨ ਦਬਾਓ ਤੇ ਬੋਲੋ। Chikoo ਬੋਲ ਕੇ ਜਵਾਬ ਦੇਵੇਗੀ।",
  pa_lat: "💡 Mic button dabao te bolo. Chikoo bol ke jawab degi.",
  hi: "💡 माइक्रोफोन दबाएँ और बोलें। Chikoo आवाज़ में जवाब देगी।",
  ur: "💡 مائیکروفون دبائیں اور بولیں۔ Chikoo آواز میں جواب دے گی۔",
};

const SPEAKING_LABEL = {
  it: "🔊 Chikoo sta parlando...",
  en: "🔊 Chikoo is speaking...",
  pa: "🔊 Chikoo ਬੋਲ ਰਹੀ ਹੈ...",
  pa_lat: "🔊 Chikoo bol rahi hai...",
  hi: "🔊 Chikoo बोल रही है...",
  ur: "🔊 Chikoo بول رہی ہے...",
};

const THINKING_LABEL = {
  it: "💭 Chikoo sta pensando...",
  en: "💭 Chikoo is thinking...",
  pa: "💭 Chikoo ਸੋਚ ਰਹੀ ਹੈ...",
  pa_lat: "💭 Chikoo soch rahi hai...",
  hi: "💭 Chikoo सोच रही है...",
  ur: "💭 Chikoo سوچ رہی ہے...",
};

const LISTENING_LABEL = {
  it: "🎤 Sto ascoltando... parla ora",
  en: "🎤 Listening... speak now",
  pa: "🎤 ਸੁਣ ਰਹੀ ਹਾਂ... ਹੁਣ ਬੋਲੋ",
  pa_lat: "🎤 Sun rahi haan... hun bolo",
  hi: "🎤 सुन रही हूँ... अब बोलिए",
  ur: "🎤 سن رہی ہوں... اب بولیے",
};

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

const VALID_ANIMS = new Set(["wave", "happy", "thinking", "talking", "idle"]);
const ANIM_TAG_REGEX = /^\s*\[anim_(\w+)\]\s*\n?/;
const stripAnimTag = (s) => s.replace(ANIM_TAG_REGEX, "");

// Silent 0.1s mp3 (base64) used to unlock the audio element on user gesture (mobile autoplay bypass)
const SILENT_MP3 =
  "data:audio/mp3;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADQADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDA/////////////////////////////////////////////////////////////////8AAAAATGF2YzU5LjE4AAAAAAAAAAAAAAAAJAJAAAAAAAAAA0DP1M/vAAAAAAAAAAAAAAAAAAAA//uQZAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";

export default function AIAssistant() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "bot", text: t.assistant.greeting }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(null);
  const [chikooAnim, setChikooAnim] = useState("idle"); // wave|happy|thinking|talking|idle
  const sessionId = useRef(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);
  const audioElRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const voiceModeRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    setMessages([{ role: "bot", text: t.assistant.greeting }]);
  }, [lang, t.assistant.greeting]);

  // Ensure we have a persistent audio element
  const getAudio = () => {
    if (!audioElRef.current) {
      const a = new Audio();
      a.preload = "auto";
      a.playsInline = true;
      audioElRef.current = a;
    }
    return audioElRef.current;
  };

  // Unlock audio playback on mobile browsers by playing a silent clip during user gesture.
  // Once unlocked, subsequent audio.play() calls (even from async contexts) will succeed.
  const unlockAudio = async () => {
    if (audioUnlockedRef.current) return;
    const a = getAudio();
    try {
      a.src = SILENT_MP3;
      a.muted = false;
      a.volume = 1.0;
      await a.play();
      a.pause();
      a.currentTime = 0;
      audioUnlockedRef.current = true;
    } catch (e) {
      // If it fails here, autoplay is fully blocked — user will still see the "Ascolta" button.
      audioUnlockedRef.current = false;
    }
  };

  // Play an MP3 blob URL through the persistent audio element
  const playUrl = async (url) => {
    const a = getAudio();
    try {
      a.pause();
    } catch (e) { console.debug("Audio pause failed:", e?.message); }
    a.src = url;
    a.currentTime = 0;
    a.onplay = () => setSpeaking(true);
    a.onended = () => setSpeaking(false);
    a.onerror = () => setSpeaking(false);
    a.onpause = () => setSpeaking(false);
    try {
      await a.play();
    } catch (err) {
      setSpeaking(false);
      // Autoplay blocked — user must tap "Ascolta"
      console.debug("Autoplay blocked:", err?.message);
    }
  };

  const speakText = async (text, mode = "fast", idx = null) => {
    if (!text) return;
    if (idx !== null) setTtsLoading(idx);
    try {
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voice: "nova", lang, mode }),
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      await playUrl(url);
    } catch (e) {
      if (idx !== null) toast.error("Errore audio");
    } finally {
      if (idx !== null) setTtsLoading(null);
    }
  };

  const send = async (customText, viaVoice = false) => {
    const text = (customText ?? input).trim();
    if (!text || loading) return;
    voiceModeRef.current = viaVoice;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "bot", text: "" }]);
    setChikooAnim("thinking");

    let fullReply = "";
    let tagExtracted = false;

    const applyDisplay = () => {
      // Once we have >= a small buffer, decide whether the tag is present.
      // If matched, extract & set anim; either way, show stripped text.
      let display = fullReply;
      if (!tagExtracted) {
        const m = fullReply.match(ANIM_TAG_REGEX);
        if (m) {
          const tag = m[1];
          if (VALID_ANIMS.has(tag)) setChikooAnim(tag);
          tagExtracted = true;
          display = fullReply.slice(m[0].length);
        } else if (fullReply.length > 24) {
          // No tag will come — accept as-is
          setChikooAnim("talking");
          tagExtracted = true;
          display = fullReply;
        } else {
          // Still waiting to see if a tag closes — hold display empty for now
          return;
        }
      } else {
        display = stripAnimTag(fullReply);
      }
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last.role === "bot") copy[copy.length - 1] = { ...last, text: display };
        return copy;
      });
    };

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, ui_lang: lang }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("event: done")) continue;
          if (line.startsWith("data:")) {
            const data = line.slice(5);
            if (data === " [DONE]" || data.trim() === "[DONE]") continue;
            const chunk = data.startsWith(" ") ? data.slice(1) : data;
            const appended = chunk + (data === "" ? "\n" : "");
            fullReply += appended;
            applyDisplay();
          }
        }
      }
      // Ensure final flush (in case reply was very short and never triggered the buffer check)
      if (!tagExtracted) {
        const m = fullReply.match(ANIM_TAG_REGEX);
        if (m && VALID_ANIMS.has(m[1])) setChikooAnim(m[1]);
        else setChikooAnim("talking");
        tagExtracted = true;
      }
      const finalText = stripAnimTag(fullReply);
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last.role === "bot") copy[copy.length - 1] = { ...last, text: finalText };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "bot", text: "Errore di connessione. Riprova." };
        return copy;
      });
      setChikooAnim("idle");
    } finally {
      setLoading(false);
      // Auto-play voice reply if user spoke — strip anim tag before speaking
      const cleanReply = stripAnimTag(fullReply).trim();
      if (voiceModeRef.current && cleanReply) {
        voiceModeRef.current = false;
        speakText(cleanReply, "fast");
      }
      // Return to idle after a short beat unless animation is looping (thinking/talking/idle)
      setTimeout(() => setChikooAnim((a) => (a === "wave" || a === "happy" ? "idle" : a)), 1600);
    }
  };

  const startListening = async () => {
    // CRITICAL: unlock audio here — we are inside a real user gesture (button tap).
    // After this, later audio.play() calls (from async fetch) will work on Android/iOS.
    await unlockAudio();

    if (!SR) {
      toast.error("Il tuo browser non supporta il microfono. Usa Chrome, Edge o Safari aggiornato.");
      return;
    }
    if (listening) {
      try { recogRef.current?.stop(); } catch (e) { console.debug("Recognition stop failed:", e?.message); }
      return;
    }
    try {
      // Explicit permission request
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Immediately release the stream — we only needed the permission prompt.
        stream.getTracks().forEach((tr) => tr.stop());
      }
    } catch (permErr) {
      console.debug("Microphone permission denied:", permErr?.message);
      toast.error("Devi consentire l'accesso al microfono per parlare a Chikoo.");
      return;
    }

    const rec = new SR();
    const primary = SPEECH_LOCALE[lang] || "en-US";
    rec.lang = primary;
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    let finalText = "";
    let didError = false;

    rec.onstart = () => setListening(true);
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      setInput((finalText + interim).trim());
    };
    rec.onerror = (e) => {
      didError = true;
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Microfono bloccato. Abilita i permessi nel browser.");
      } else if (e.error === "no-speech") {
        toast.info("Non ho sentito nulla. Riprova più forte.");
      } else if (e.error === "language-not-supported" && SPEECH_FALLBACK[lang]) {
        // Retry with fallback language (e.g., Punjabi -> Hindi on some Android devices)
        try {
          const rec2 = new SR();
          rec2.lang = SPEECH_FALLBACK[lang];
          rec2.continuous = false;
          rec2.interimResults = true;
          rec2.onstart = () => setListening(true);
          rec2.onresult = rec.onresult;
          rec2.onerror = () => setListening(false);
          rec2.onend = () => {
            setListening(false);
            const t = finalText.trim();
            if (t) send(t, true);
          };
          recogRef.current = rec2;
          rec2.start();
        } catch (err) { console.debug("Fallback recognition failed:", err?.message); }
      } else if (e.error !== "aborted") {
        toast.error(`Errore microfono: ${e.error}`);
      }
    };
    rec.onend = () => {
      setListening(false);
      const txt = finalText.trim();
      if (txt && !didError) {
        // Send immediately — no setTimeout, so we preserve the promise chain from user gesture
        send(txt, true);
      }
    };
    recogRef.current = rec;
    try {
      rec.start();
    } catch (e) {
      setListening(false);
      toast.error("Impossibile avviare il microfono. Riprova.");
    }
  };

  const openAssistant = async () => {
    setOpen(true);
    setChikooAnim("wave");
    setTimeout(() => setChikooAnim("idle"), 2200);
    // Unlock audio proactively when the panel opens (user tapped the FAB)
    await unlockAudio();
  };

  return (
    <>
      <button
        onClick={openAssistant}
        className="fixed bottom-6 right-6 z-50 rounded-full brand-blue-bg text-white shadow-xl hover:scale-105 transition-transform flex items-center gap-2 pl-1 pr-4 py-1 group"
        data-testid="ai-assistant-fab"
        aria-label="Open Chikoo Assistant"
      >
        <span className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/40 bg-white">
          <img src="/chikoo_mascot.png" alt="Chikoo" className="w-full h-full object-cover" />
          <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 ring-2 ring-[#0B3D91]" />
        </span>
        <span className="font-display font-bold">Chikoo</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-end sm:justify-end p-0 sm:p-6 bg-black/40" onClick={() => setOpen(false)} data-testid="ai-assistant-modal">
          <div
            className="w-full sm:w-[440px] h-[85vh] sm:h-[640px] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="brand-blue-bg text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative w-11 h-11 rounded-full overflow-hidden ring-2 ring-white/40 bg-white">
                  <img src="/chikoo_mascot.png" alt="Chikoo" className={`w-full h-full object-cover anim-${speaking ? "talking" : chikooAnim}`} />
                </div>
                <div>
                  <div className="font-display font-bold">{t.assistant.title}</div>
                  <div className="text-xs opacity-90">{t.assistant.subtitle}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="ai-assistant-close">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 brand-sky-bg/50" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "bot" && (
                    <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 bg-white ring-1 ring-[#F97316]/30">
                      <img src="/chikoo_mascot.png" alt="Chikoo" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className={`${m.role === "user" ? "bubble-user" : "bubble-bot"} px-4 py-2.5 max-w-[80%] text-sm whitespace-pre-wrap relative group`}>
                    {m.text || (loading && i === messages.length - 1 ? "..." : "")}
                    {m.role === "bot" && m.text && !loading && (
                      <button
                        onClick={() => speakText(m.text, "hd", i)}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[#0B3D91] hover:text-[#F97316] font-semibold"
                        data-testid={`bot-speak-${i}`}
                        title="Ascolta"
                      >
                        <Volume2 size={12} /> {ttsLoading === i ? "..." : "Ascolta"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {(listening || loading || speaking) && (
              <div className="mx-3 mb-2 py-2 px-3 rounded-xl bg-[#FFF4EC] text-[#F97316] text-xs font-semibold flex items-center gap-2 justify-center" data-testid="chikoo-status">
                <span className="w-2 h-2 rounded-full bg-[#F97316] animate-pulse" />
                {listening
                  ? (LISTENING_LABEL[lang] || LISTENING_LABEL.en)
                  : speaking
                  ? (SPEAKING_LABEL[lang] || SPEAKING_LABEL.en)
                  : (THINKING_LABEL[lang] || THINKING_LABEL.en)}
              </div>
            )}

            <div className="p-3 border-t border-[#E4EAF7] flex gap-2 items-center">
              <button
                onClick={startListening}
                disabled={loading}
                className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                  listening ? "bg-[#F97316] text-white animate-pulse" : "brand-blue-bg text-white hover:bg-[#082F76]"
                } disabled:opacity-50`}
                data-testid="ai-mic-btn"
                title={listening ? "Ferma" : "Parla con Chikoo"}
                aria-label="Voice input"
              >
                {listening ? <MicOff size={18} /> : <Mic size={18} />}
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.assistant.placeholder}
                className="warm-input !py-2.5"
                data-testid="ai-chat-input"
                disabled={loading}
              />
              <button onClick={() => send()} disabled={loading || !input.trim()} className="btn-primary !py-2.5 !px-4 disabled:opacity-50 shrink-0" data-testid="ai-chat-send">
                <Send size={16} />
              </button>
            </div>
            <div className="px-4 pb-3 text-[11px] text-[#8494B8] text-center">
              {MIC_HINT[lang] || MIC_HINT.en}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
